import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { capability, dfyBundle, FESTIVAL_STACK_MONTHLY } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const ticketing = capability('ticketing')!
const concierge = dfyBundle('concierge')

const INLINE_LINK =
  'text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

const PROBLEMS = [
  {
    problem: 'Ticket fees embarrass the box office',
    description:
      'General-purpose ticketing platforms stack a service fee, a per-ticket charge, and processing on every order. Your patrons see it at checkout, and it reads as your fee.',
  },
  {
    problem: 'Your concert lives on someone else’s page',
    description:
      'Buyers get bounced to a third-party checkout with someone else’s branding, upsells for other events, and no way back to your site.',
  },
  {
    problem: 'The audience data stays with the vendor',
    description:
      'Who bought, who came, who returns every season: on most platforms that history belongs to the ticketing company, not to you.',
  },
  {
    problem: 'Event night is still manual',
    description: 'Will-call lists printed at midnight, no check-in flow, and no report the morning after.',
  },
]

const FAQS = [
  {
    q: 'What does ticketing cost?',
    a: `$${ticketing.priceMonthly} a month plus ${ticketing.perUnitFee}. Selling a ticket costs nothing beyond the 1.5%; the monthly price is box-office management: tiers, capacity, QR check-in, and reporting. Stripe card processing applies as with any payment.`,
  },
  {
    q: 'Who runs it?',
    a: `Today our team sets up and runs ticketing for you; self-serve controls are rolling out. If you want event operations fully off your plate, that lives in Concierge (${concierge.price}). Festivals usually add ticketing to the rest of the stack, which comes to $${FESTIVAL_STACK_MONTHLY}/mo all in.`,
  },
  {
    q: 'Do buyers leave our website?',
    a: 'No. Tickets sell on your site, under your brand, and the buyer lands back in your audience record alongside their email and giving history.',
  },
  {
    q: 'What about free events and RSVPs?',
    a: 'RSVP events carry no per-ticket fee. You only see the 1.5% when money changes hands.',
  },
]

export default function TicketingPage() {
  const breadcrumbs = generateBreadcrumbs('/services/ticketing', breadcrumbLabels)

  useSEO({
    title: 'Ticketing Software for Concerts and Music Festivals',
    // Prices interpolate from pricing.ts; this was a literal $25/mo + 1.5%.
    description: `Box office software for orchestras, festivals, and ensembles: sell tickets on your own website for $${ticketing.priceMonthly}/mo plus ${ticketing.perUnitFee}, with QR check-in and reporting. Far below big-platform fee stacks, and your audience data stays yours.`,
    url: '/services/ticketing',
    canonical: '/services/ticketing',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Ticketing Software for Concerts and Music Festivals',
      description:
        'Concert and event ticketing on your own website with flat monthly pricing, 1.5% per ticket, and QR check-in, with full box-office operations available in Concierge engagements.',
      url: '/services/ticketing',
    }),
  })

  // The hero keeps its entrance; everything below the fold is static. The
  // per-section staggered reveals and card hover lifts were the stock kit.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-16 md:space-y-36 md:px-12">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Sell tickets on your own site</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your concerts, your checkout, your audience data. Tiers, capacity, QR check-in, and reporting, with 1.5% per
          ticket instead of a platform fee stack.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          ${ticketing.priceMonthly} a month for box-office management. Selling a ticket costs nothing beyond the 1.5%,
          and RSVPs for free events cost nothing at all.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/compare/eventbrite">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Compare vs Eventbrite
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems: a typographic list, name left and explanation right, in
          place of four bordered cards. Space alone separates the rows. */}
      <section aria-labelledby="problems" className="space-y-12">
        <h2 id="problems" className="text-fluid-h3 max-w-3xl">
          Why not just use a ticketing platform?
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {PROBLEMS.map((item) => (
            <li key={item.problem} className="grid gap-3 md:grid-cols-12 md:items-baseline md:gap-8">
              <h3 className="text-fluid-h4 md:col-span-5">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.description}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Unified CRM differentiator */}
      <section aria-labelledby="one-record" className="grid gap-6 md:grid-cols-12 md:gap-8">
        <h2 id="one-record" className="text-fluid-h3 md:col-span-5">
          Every buyer joins your audience
        </h2>
        <div className="text-muted-foreground space-y-4 leading-relaxed md:col-span-7">
          <p className="text-fluid-body max-w-[60ch]">
            Ticket buyers land in the same audience record as your subscribers and donors. Next season you can email
            everyone who came last year, and your year-end appeal can start with the people who already buy tickets.
          </p>
          <p className="max-w-[60ch]">
            Pairs with{' '}
            <Link to="/services/email-marketing" className={INLINE_LINK}>
              Email Marketing
            </Link>{' '}
            and{' '}
            <Link to="/services/donor-management" className={INLINE_LINK}>
              Donor Management
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Pricing. Two genuinely comparable plans, so they stay side by side on
          a plain ground; the recommended one is set apart by a raised ground,
          not a tint, glow or hover lift. */}
      <section aria-labelledby="pricing" className="space-y-12">
        <h2 id="pricing" className="text-fluid-h3">
          Two ways to run it
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-card flex flex-col rounded-md p-8">
            <h3 className="text-fluid-h4">{ticketing.name}</h3>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-5xl leading-none">${ticketing.priceMonthly}</span>
              <span className="text-muted-foreground">/month + {ticketing.perUnitFee}</span>
            </p>
            <ul className="text-muted-foreground mt-6 mb-8 flex-1 space-y-2 leading-relaxed">
              {[
                'Events, tiers, capacity',
                'QR check-in on event night',
                'Sales reporting',
                'Free events and RSVPs carry no per-ticket fee',
                'Set up and run for you by our team today',
              ].map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/get-started" className="block">
              <Button className="w-full cursor-pointer">Get Started</Button>
            </Link>
          </div>
          <div className="bg-surface flex flex-col rounded-md p-8">
            <h3 className="text-fluid-h4">Box office, run for you</h3>
            <p className="text-muted-foreground mt-1 text-sm">Done for you</p>
            <p className="mt-6 space-y-1">
              <span className="font-display block text-3xl leading-tight">{concierge.name}</span>
              <span className="text-muted-foreground block">{concierge.price}</span>
            </p>
            <ul className="text-muted-foreground mt-6 mb-8 flex-1 space-y-2 leading-relaxed">
              {[
                'Event setup and on-sale management',
                'Reporting through the season',
                'Check-in support on event day',
                `Festivals: part of the full stack at $${FESTIVAL_STACK_MONTHLY}/mo`,
              ].map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/services/done-for-you#concierge" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
        <p className="text-muted-foreground text-sm">
          Product details live on the{' '}
          <Link to="/platform/features/events" className={INLINE_LINK}>
            Events feature page
          </Link>
          . Pay annually on any plan and get 2 months free.
        </p>
      </section>

      {/* FAQ */}
      <section aria-labelledby="questions" className="space-y-12">
        <h2 id="questions" className="text-fluid-h3">
          Questions
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {FAQS.map((f) => (
            <li key={f.q} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl leading-snug md:col-span-5">{f.q}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{f.a}</p>
            </li>
          ))}
        </ul>
        <div className="space-y-3">
          <a href="/book" target="_blank" rel="noopener noreferrer" className="inline-block">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
